import { Link } from "react-router-dom";
import { Shield, Mic, BarChart3, Swords, Brain, Zap, Lock, ArrowRight, Activity, Eye, Users, Tv, Search, MessageSquare, Landmark, GraduationCap, ChevronLeft, ChevronRight, Play, Pause, ExternalLink, X, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import NavigationBar from "@/components/NavigationBar";
import AnimatedTranslationText from "@/components/AnimatedTranslationText";
import NewsletterSignup from "@/components/NewsletterSignup";
import EliteFooter from "@/components/EliteFooter";
import AboutTheBrand from "@/components/AboutTheBrand";
import SelfDebateStudioPreview from "@/components/SelfDebateStudioPreview";
import { useState, useEffect, useRef, useCallback } from "react";

import lincolnImg from "@/assets/lincoln-speech.jpg";
import obamaImg from "@/assets/obama-speech.jpg";
import trumpImg from "@/assets/trump-speech.jpg";
import jfkImg from "@/assets/jfk-speech.jpg";
import reaganImg from "@/assets/reagan-speech.jpg";
import bushImg from "@/assets/bush-speech.jpg";
import bushSrImg from "@/assets/bush-sr-speech.jpg";
import bidenImg from "@/assets/biden-speech.jpg";
import heroVideoBg from "@/assets/presidential-montage-bg.mp4.asset.json";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import theFloorLogo from "@/assets/the-floor-logo.png";
import HeroDebateShowcase from "@/components/HeroDebateShowcase";
import YouTubeFeatureSection from "@/components/YouTubeFeatureSection";
import { useYouTubeReachable } from "@/hooks/useYouTubeReachable";
import { WifiOff } from "lucide-react";
import Seo from "@/components/Seo";

/* ─── Presidential Carousel Data ─── */
const presidents = [
  { name: "Abraham Lincoln", title: "Gettysburg Address, 1863", videoId: "UKjmT77y6jI", quote: "Government of the people, by the people, for the people.", image: lincolnImg },
  { name: "John F. Kennedy", title: "Inaugural Address, 1961", videoId: "BLmiOEk59n8", quote: "Ask not what your country can do for you...", image: jfkImg },
  { name: "Ronald Reagan", title: "Berlin Wall, 1987", videoId: "5MDFX-dNtsM", quote: "Mr. Gorbachev, tear down this wall!", image: reaganImg },
  { name: "George H.W. Bush", title: "Inaugural Address, 1989", videoId: "hbG_bBaLGCo", quote: "A new breeze is blowing, and a nation refreshed by freedom stands ready to push on.", image: bushSrImg },
  { name: "Barack Obama", title: "Victory Speech, 2008", videoId: "LEcGJRaBYJo", quote: "Yes we can.", image: obamaImg },
  { name: "George W. Bush", title: "9/11 Address, 2001", videoId: "YMiqEUBux3o", quote: "We will not tire, we will not falter, and we will not fail.", image: bushImg },
  { name: "Donald Trump", title: "Inaugural Address, 2017", videoId: "sRBsJNdK1t0", quote: "We will make America great again.", image: trumpImg },
  { name: "Joe Biden", title: "Inaugural Address, 2021", videoId: "q5iCPKDp4V4", quote: "Democracy has prevailed.", image: bidenImg },
];

/* ─── Presidential Debates Data ─── */
const presidentialDebates = [
  { title: "Biden vs Trump", subtitle: "2024 Presidential Debate", year: "2024", videoId: "qqG96G8YdcE", desc: "The first 2024 presidential debate between President Biden and former President Trump." },
  { title: "Trump vs Harris", subtitle: "2024 ABC Presidential Debate", year: "2024", videoId: "GdSDngmDLmY", desc: "The ABC News presidential debate between Trump and Vice President Harris." },
  { title: "Biden vs Trump", subtitle: "First 2020 Presidential Debate", year: "2020", videoId: "wW1lY5jFNcQ", desc: "Historic clash in Cleveland between President Trump and former VP Biden." },
  { title: "Obama vs Romney", subtitle: "First Presidential Debate", year: "2012", videoId: "KfaBRyCKRhk", desc: "President Obama faces off against Governor Mitt Romney in Denver." },
  { title: "Biden vs Palin", subtitle: "2008 Vice Presidential Debate", year: "2008", videoId: "eOombKZSsj4", desc: "Senator Joe Biden debates Governor Sarah Palin in the historic 2008 VP debate." },
  { title: "Bush vs Kerry", subtitle: "First Presidential Debate", year: "2004", videoId: "WYpP-T0IcyA", desc: "President George W. Bush debates Senator John Kerry on foreign policy." },
  { title: "Bush vs Gore", subtitle: "First Presidential Debate", year: "2000", videoId: "HwQBeMUj_ps", desc: "Governor George W. Bush vs Vice President Al Gore in the pivotal 2000 election." },
  { title: "Kennedy vs Nixon", subtitle: "First Televised Debate", year: "1960", videoId: "AYP8-oxq8ig", desc: "The first-ever televised presidential debate that changed politics forever." },
  { title: "Reagan vs Carter", subtitle: "Presidential Debate", year: "1980", videoId: "_8YxFc_1b_0", desc: "Reagan's famous 'There you go again' moment that defined the 1980 election." },
  { title: "Lincoln vs Douglas", subtitle: "Historic Senate Debates", year: "1858", videoId: "NW_3iFwOz8Y", desc: "HISTORY documentary on the legendary Lincoln-Douglas debates and slavery." },
];

/* ─── Counter Hook ─── */
const useCountUp = (target: number, duration = 2000) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setStarted(true); obs.disconnect(); } }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = target / (duration / 16);
    const id = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(id); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(id);
  }, [started, target, duration]);

  return { count, ref };
};

const CounterStat = ({ value, suffix, label }: { value: number; suffix: string; label: string }) => {
  const { count, ref } = useCountUp(value);
  return (
    <div ref={ref} className="text-center">
      <p className="text-3xl md:text-4xl font-black text-sovereign-gold">{count.toLocaleString()}{suffix}</p>
      <p className="text-xs text-muted-foreground font-mono mt-1">{label}</p>
    </div>
  );
};

const suiteModules = [
  { title: "Oratory Studio", desc: "Pre-speech preparation with The Floor AI Coach. Upload text, audio, or video for real-time corrections on clarity, logic, facts, tone, pacing, and gestures.", icon: Mic, path: "/oratory-studio", variant: "cyan" as const, status: "READY", features: ["Floor AI Coach", "Multi-modal input", "Drift Sentinel"] },
  { title: "Situation Room", desc: "Post-presentation after-action intelligence. Delivery forensics, micro-expression timeline, audience impact prediction, and intelligence briefs.", icon: BarChart3, path: "/situation-room", variant: "gold" as const, status: "READY", features: ["Analyzer Report", "Electoral Impact", "Resilience EKG"] },
  { title: "The Arena", desc: "Livestream debate controls with AgoraL Smart Moderator, Press Gauntlet overlay, and real-time dominance scoring.", icon: Swords, path: "/arena", variant: "cyan" as const, status: "READY", features: ["AgoraL Moderator", "Press Gauntlet", "Debate Timer"] },
  { title: "Smart Chat", desc: "Full-featured AI chat with conversation starters, quick reactions, translation, AI writer tools, audio controls, and multimedia sharing.", icon: MessageSquare, path: "/smart-chat", variant: "cyan" as const, status: "READY", features: ["Conversation Starters", "AI Writer Tools", "Translation"] },
  { title: "Watch Now", desc: "Netflix-style debate catalog. Browse dual, panel, and round table formats from 2 to 20 participants with full AI analysis overlay.", icon: Tv, path: "/watch", variant: "gold" as const, status: "READY", features: ["Debate Templates", "Round Table", "Council of Ministers"] },
  { title: "Cabinet Council", desc: "Council of Ministers virtual chamber with round table seating, portfolio agendas, constitutional checks, and diplomatic protocols for 6–24 seats.", icon: Landmark, path: "/virtual-meetings?tab=cabinet", variant: "gold" as const, status: "READY", features: ["Round Table", "9 Protocols", "Veto Power"] },
  { title: "Floor Trainer", desc: "Elite speech coaching for presidents, CEOs, and governors. Private sessions with world-class coaches, AI-augmented feedback, and battle-tested programs.", icon: GraduationCap, path: "/floor-trainer", variant: "cyan" as const, status: "READY", features: ["Elite Coaches", "AI Feedback", "Programs"] },
];

const capabilities = [
  { icon: Brain, label: "Micro-Expression Engine", desc: "Sub-120ms behavioral detection" },
  { icon: Activity, label: "Dominance Score", desc: "Real-time rhetorical control metric" },
  { icon: Eye, label: "Cognitive Drift Sentinel", desc: "Semantic trajectory monitoring" },
  { icon: Users, label: "Electoral Impact Score", desc: "Undecided voter digital twin" },
  { icon: Shield, label: "Sovereign Security", desc: "Zero-knowledge, air-gapped ready" },
  { icon: Zap, label: "Clip Vulnerability Index", desc: "Attack ad risk detection" },
];

const Index = () => {
  const [search, setSearch] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [activeVideo, setActiveVideo] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  // Hero emblem recurring popup: every 60 seconds the logo pops in,
  // stays visible for 3 seconds, then fades out. We keep two flags so
  // we can fade out gracefully before unmounting the <img> entirely.
  const [logoVisible, setLogoVisible] = useState(true);
  const [logoMounted, setLogoMounted] = useState(true);
  // Preflight: can the browser/network actually reach youtube.com?
  // Used to render a friendly banner above the hero embeds.
  const ytReachable = useYouTubeReachable();
  // We track timer ids in a ref so we can cancel any in-flight fade /
  // unmount before scheduling the next pop-in.
  const logoTimers = useRef<{ fade?: number; unmount?: number }>({});
  // Visible window for each pop: 3 seconds. After that we fade out
  // (1.2s transition) and then unmount until the next 60s tick.
  const LOGO_VISIBLE_MS = 3_000;
  const LOGO_FADE_MS = 1_200;
  const LOGO_INTERVAL_MS = 60_000;
  const playLogo = useCallback(() => {
    if (logoTimers.current.fade) window.clearTimeout(logoTimers.current.fade);
    if (logoTimers.current.unmount) window.clearTimeout(logoTimers.current.unmount);
    setLogoMounted(true);
    setLogoVisible(true);
    logoTimers.current.fade = window.setTimeout(() => setLogoVisible(false), LOGO_VISIBLE_MS);
    logoTimers.current.unmount = window.setTimeout(
      () => setLogoMounted(false),
      LOGO_VISIBLE_MS + LOGO_FADE_MS,
    );
  }, []);
  useEffect(() => {
    // Initial pop on mount, then re-pop every 60 seconds.
    playLogo();
    const interval = window.setInterval(playLogo, LOGO_INTERVAL_MS);
    return () => {
      window.clearInterval(interval);
      if (logoTimers.current.fade) window.clearTimeout(logoTimers.current.fade);
      if (logoTimers.current.unmount) window.clearTimeout(logoTimers.current.unmount);
    };
  }, [playLogo]);

  const nextSlide = useCallback(() => setCurrentSlide(p => (p + 1) % presidents.length), []);
  const prevSlide = useCallback(() => setCurrentSlide(p => (p - 1 + presidents.length) % presidents.length), []);

  useEffect(() => {
    if (!isPlaying) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(nextSlide, 6000);
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, nextSlide]);

  const filteredModules = suiteModules.filter(m =>
    m.title.toLowerCase().includes(search.toLowerCase()) ||
    m.desc.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background grid-tactical">
      <Seo
        path="/"
        title="The Floor — Sovereign AI Speech & Debate Platform for Leaders"
        description="Rehearse speeches, run live debates, and master public discourse with real-time AI coaching built for presidents, politicians, and executives."
      />
      <NavigationBar />

      <main className="pt-14">
        {/* Hero with Presidential Carousel Background */}
        <section className="relative px-6 py-24 max-w-[1200px] mx-auto text-center overflow-hidden min-h-[600px]">
          {/* Dynamic president background — Ken Burns zoom with crossfade */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Cinematic AI-generated video background */}
            <video
              autoPlay muted loop playsInline
              className="absolute inset-0 w-full h-full object-cover opacity-40"
              src={heroVideoBg.url}
            />
            {/* President image overlay synced to carousel */}
            {presidents.map((p, i) => {
              const isActive = i === currentSlide;
              const isNext = i === (currentSlide + 1) % presidents.length;
              if (!isActive && !isNext) return <div key={p.videoId} className="absolute inset-0 opacity-0" />;
              return (
                <div
                  key={p.videoId}
                  style={{ transitionDuration: '1800ms' }}
                  className={`absolute inset-0 transition-opacity ease-in-out ${isActive ? "opacity-50" : "opacity-0"}`}
                >
                  <img
                    src={p.image}
                    alt=""
                    className={`absolute inset-0 w-full h-full object-cover ${isActive ? "animate-ken-burns" : ""}`}
                  />
                </div>
              );
            })}
            <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/20 to-background/70" />
          </div>

          <div className="relative z-10">
            {/* The Floor — Sovereign Intelligence Suite emblem.
                Pops in every 60 s and auto-hides after 3 s. */}
            {logoMounted && (
              <div
                className={`flex justify-center mb-6 transition-all duration-[1200ms] ease-out ${
                  logoVisible ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
                }`}
              >
                <img
                  src={theFloorLogo}
                  alt="The Floor — Sovereign Intelligence Suite"
                  className="w-40 md:w-56 h-auto drop-shadow-[0_0_30px_hsl(var(--sovereign-gold)/0.45)] animate-fade-in"
                  loading="eager"
                  decoding="async"
                />
              </div>
            )}
            {/* Replay button — re-triggers the 3-second logo pop-in. */}
            <div className="flex justify-center mb-4">
              <button
                type="button"
                onClick={playLogo}
                className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-sovereign-gold transition-colors px-2 py-1 rounded-full border border-border/40 hover:border-sovereign-gold/40"
                aria-label="Replay logo pop-in"
                data-testid="replay-logo-button"
              >
                <RotateCcw className="w-3 h-3" /> Replay logo
              </button>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-sovereign-gold/20 bg-sovereign-gold/5 text-sovereign-gold text-xs font-mono mb-6">
              <Lock className="w-3 h-3" /> SOVEREIGN TIER — CLASSIFIED
            </div>
            <AnimatedTranslationText
              className="text-5xl md:text-7xl font-black tracking-tight mb-4"
              subtitleClassName="text-xl text-muted-foreground max-w-2xl mx-auto mb-2"
            />
            <p className="text-sm text-muted-foreground max-w-xl mx-auto mb-3">
              Dual AI coaching, real-time debate control, psychological audience modeling, and sovereign security for high-stakes oratory.
            </p>
            <p className="text-xs font-mono text-muted-foreground mb-6">
              "Others analyze speeches after the damage. The Floor prevents the damage in real time."
            </p>

            {/* Search Bar */}
            <div className="max-w-lg mx-auto mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search modules, features, tools..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-10 h-11 bg-card border-border rounded-full text-sm"
                />
              </div>
            </div>

            <div className="flex items-center justify-center gap-3 mt-8 flex-wrap">
              <Link to="/oratory-studio"><Button variant="sovereign" size="lg" className="gap-2"><Brain className="w-4 h-4" /> Enter Oratory Studio</Button></Link>
              <Link to="/situation-room"><Button variant="outline-gold" size="lg" className="gap-2"><BarChart3 className="w-4 h-4" /> Situation Room</Button></Link>
              <Link to="/watch"><Button variant="outline-cyan" size="lg" className="gap-2"><Tv className="w-4 h-4" /> Watch Now</Button></Link>
              <Link to="/arena"><Button variant="outline-cyan" size="lg" className="gap-2"><Swords className="w-4 h-4" /> Open Arena</Button></Link>
              <Link to="/smart-chat"><Button variant="outline" size="lg" className="gap-2"><MessageSquare className="w-4 h-4" /> Smart Chat</Button></Link>
            </div>

            {/* Hero debate showcase — featured player + 9-thumbnail strip.
                Auto-falls back to Mux mirror when YouTube refuses the embed. */}
            <div className="mt-12">
              <p className="mb-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                Historic Confrontations · Watch the Floor in Action
              </p>
              {ytReachable === false && (
                <div
                  role="status"
                  data-testid="yt-preflight-banner"
                  className="mx-auto mb-4 flex max-w-2xl items-start gap-3 rounded-lg border border-tactical-amber/40 bg-tactical-amber/10 p-3 text-left"
                >
                  <WifiOff className="h-4 w-4 mt-0.5 text-tactical-amber shrink-0" />
                  <div className="text-xs text-foreground">
                    <p className="font-bold">YouTube is unreachable from this network.</p>
                    <p className="text-muted-foreground">
                      We couldn't reach youtube.com. You may be offline, behind a firewall,
                      or using an extension that blocks it. Embeds may not load — use the
                      "Open on YouTube" links instead.
                    </p>
                  </div>
                </div>
              )}
              <HeroDebateShowcase debates={presidentialDebates} />
            </div>
          </div>
        </section>

        {/* ═══ Presidential Carousel ═══ */}
        <section className="px-6 pb-16 max-w-[1200px] mx-auto">
          <h2 className="text-xs font-mono uppercase tracking-widest text-muted-foreground text-center mb-8">
            Great American Speeches
          </h2>
          <div className="relative">
            {/* Cards */}
            <div className="overflow-hidden rounded-2xl border border-sovereign-gold/20 bg-card">
              <div className="relative" style={{ minHeight: 380 }}>
                {presidents.map((p, i) => (
                  <div key={p.videoId} className={`absolute inset-0 transition-all duration-700 ${i === currentSlide ? "opacity-100 translate-x-0 z-10" : i < currentSlide ? "opacity-0 -translate-x-full pointer-events-none" : "opacity-0 translate-x-full pointer-events-none"}`}>
                    <div className="flex flex-col md:flex-row items-center h-full min-h-[380px]">
                      {/* Video thumbnail side */}
                      <div className="relative w-full md:w-1/2 h-[220px] md:h-[380px] bg-gradient-to-br from-sovereign-gold/10 to-background overflow-hidden">
                        <img
                          src={p.image}
                          alt={p.name}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/80" />
                        <button
                          onClick={() => { setActiveVideo(i); setIsPlaying(false); }}
                          aria-label={`Play ${p.name} — ${p.title}`}
                          className="absolute inset-0 flex items-center justify-center z-10 group">
                          <div className="w-16 h-16 rounded-full bg-sovereign-gold/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                            <Play className="w-7 h-7 text-background ml-1" />
                          </div>
                        </button>
                      </div>
                      {/* Info side */}
                      <div className="flex-1 p-8 flex flex-col justify-center">
                        <p className="text-[10px] font-mono text-sovereign-gold mb-2">{p.title}</p>
                        <h3 className="text-3xl font-black mb-3">{p.name}</h3>
                        <p className="text-lg italic text-muted-foreground mb-6">"{p.quote}"</p>
                        <button onClick={() => { setActiveVideo(i); setIsPlaying(false); }}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sovereign-gold text-background text-sm font-bold hover:bg-sovereign-gold/80 transition-colors w-fit">
                          <Play className="w-4 h-4" /> Watch Full Speech
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <button onClick={prevSlide} aria-label="Previous speech" className="w-10 h-10 rounded-full border border-border bg-card flex items-center justify-center hover:border-sovereign-gold/40 transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={nextSlide} aria-label="Next speech" className="w-10 h-10 rounded-full border border-border bg-card flex items-center justify-center hover:border-sovereign-gold/40 transition-colors">
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button onClick={() => { setIsPlaying(p => !p); setActiveVideo(null); }}
                  aria-label={isPlaying ? "Pause carousel" : "Play carousel"}
                  className="w-10 h-10 rounded-full border border-border bg-card flex items-center justify-center hover:border-sovereign-gold/40 transition-colors">
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                </button>
              </div>
              {/* Dots */}
              <div className="flex items-center gap-2">
                {presidents.map((_, i) => (
                  <button key={i} onClick={() => { setCurrentSlide(i); setActiveVideo(null); }}
                    aria-label={`Go to speech ${i + 1} of ${presidents.length}`}
                    aria-current={i === currentSlide ? "true" : undefined}
                    className={`h-2 rounded-full transition-all duration-300 ${i === currentSlide ? "w-8 bg-sovereign-gold" : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"}`} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══ Featured YouTube Section ═══
            Standalone, responsive, centered video section. Uses the same
            preflight + per-video fallback as the hero showcase. */}
        <YouTubeFeatureSection
          eyebrow="Featured · YouTube"
          heading="Watch & Replay"
          videos={presidentialDebates.map(d => ({
            title: d.title,
            subtitle: `${d.subtitle} · ${d.year}`,
            videoId: d.videoId,
          }))}
        />

        {/* ═══ Stats with Counter Animation ═══ */}
        <section className="px-6 pb-16 max-w-[1200px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 panel-tactical p-8">
            <CounterStat value={50000} suffix="+" label="SPEECHES ANALYZED" />
            <CounterStat value={1000000} suffix="+" label="WORDS PROCESSED" />
            <CounterStat value={2500} suffix="+" label="ELITE USERS" />
            <CounterStat value={99} suffix="%" label="ACCURACY RATE" />
          </div>
        </section>

        {/* Capabilities Grid */}
        <section className="px-6 pb-12 max-w-[1200px] mx-auto">
          <h2 className="text-xs font-mono uppercase tracking-widest text-muted-foreground text-center mb-6">Sovereign Capabilities</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {capabilities.map((cap, i) => (
              <div key={cap.label} className="panel-tactical p-3 text-center animate-fade-in-up" style={{ animationDelay: `${i * 80}ms` }}>
                <cap.icon className="w-5 h-5 text-sovereign-gold mx-auto mb-2" />
                <p className="text-xs font-semibold mb-0.5">{cap.label}</p>
                <p className="text-[10px] text-muted-foreground">{cap.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Modules Grid */}
        <section className="px-6 pb-16 max-w-[1200px] mx-auto">
          <div className="grid md:grid-cols-3 gap-4">
            {filteredModules.map((mod, i) => (
              <Link key={mod.title} to={mod.path}
                className="panel-tactical p-6 hover:border-sovereign-gold/30 transition-all group animate-fade-in-up"
                style={{ animationDelay: `${i * 100}ms` }}>
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${mod.variant === "gold" ? "bg-sovereign-gold/10 border border-sovereign-gold/30" : "bg-ai-cyan/10 border border-ai-cyan/30"}`}>
                    <mod.icon className={`w-5 h-5 ${mod.variant === "gold" ? "text-sovereign-gold" : "text-ai-cyan"}`} />
                  </div>
                  <span className="text-[10px] font-mono text-tactical-green">{mod.status}</span>
                </div>
                <h3 className="text-lg font-bold mb-1">{mod.title}</h3>
                <p className="text-sm text-muted-foreground mb-3">{mod.desc}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {mod.features.map(f => <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground font-mono">{f}</span>)}
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-sovereign-gold transition-colors">
                  Enter <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ═══ Presidential Debates Section ═══ */}
        <section className="px-6 pb-16 max-w-[1200px] mx-auto">
          <h2 className="text-xs font-mono uppercase tracking-widest text-muted-foreground text-center mb-2">
            Historic Confrontations
          </h2>
          <h3 className="text-2xl font-black text-center mb-8">Presidential Debates</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {presidentialDebates.map((debate, i) => (
              <div key={`${debate.title}-${debate.year}`}
                className="panel-tactical overflow-hidden group hover:border-sovereign-gold/30 transition-all animate-fade-in-up"
                style={{ animationDelay: `${i * 100}ms` }}>
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={`https://img.youtube.com/vi/${debate.videoId}/hqdefault.jpg`}
                    alt={`${debate.title} ${debate.year}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                  <a href={`https://www.youtube.com/watch?v=${debate.videoId}`} target="_blank" rel="noopener noreferrer"
                    aria-label={`Play ${debate.title} ${debate.year} on YouTube`}
                    className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-sovereign-gold/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 text-background ml-0.5" />
                    </div>
                  </a>
                  <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-background/70 backdrop-blur-sm text-[10px] font-mono">{debate.year}</span>
                </div>
                <div className="p-4">
                  <p className="text-xs font-mono text-sovereign-gold mb-1">{debate.subtitle}</p>
                  <h4 className="text-sm font-bold mb-1">{debate.title}</h4>
                  <p className="text-xs text-muted-foreground mb-3">{debate.desc}</p>
                  <a href={`https://www.youtube.com/watch?v=${debate.videoId}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-ai-cyan hover:underline">
                    <Play className="w-3 h-3" /> Watch Full Debate <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        <NewsletterSignup />
        <AboutTheBrand />
        <SelfDebateStudioPreview />
        <EliteFooter />
      </main>

      {/* Hidden Sentry smoke-test trigger — off-screen but keyboard reachable */}
      <button
        type="button"
        aria-label="Trigger Sentry test error"
        onClick={() => { throw new Error("Test Sentry Error"); }}
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
        data-testid="sentry-test-button"
      >
        Trigger Sentry Test Error
      </button>

      {/* Fullscreen Video Modal */}
      {activeVideo !== null && (
        <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center animate-fade-in">
          <div className="relative w-full max-w-5xl mx-4 aspect-video rounded-2xl overflow-hidden border border-sovereign-gold/30 shadow-2xl">
            <YouTubeEmbed
              videoId={presidents[activeVideo].videoId}
              title={presidents[activeVideo].name}
              className="w-full h-full"
            />
          </div>
          {/* Close button */}
          <button
            onClick={() => { setActiveVideo(null); setIsPlaying(true); }}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-card border border-border flex items-center justify-center hover:border-sovereign-gold/40 transition-colors group"
          >
            <X className="w-6 h-6 text-muted-foreground group-hover:text-foreground" />
          </button>
          {/* President info overlay */}
          <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between pointer-events-none">
            <div>
              <p className="text-[10px] font-mono text-sovereign-gold mb-1">{presidents[activeVideo].title}</p>
              <h3 className="text-xl font-black">{presidents[activeVideo].name}</h3>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
